import { Component, OnInit, Optional, OnDestroy, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { FormGroup } from "@angular/forms";
import { Subscription, Observable, Subject, of } from "rxjs";
import { tap, map, delay } from "rxjs/operators";
import { BaseControlComponent } from "../base-control/base-control.component";
import { ContextInteractionService } from "../../services/context-interaction.service";
import { FlowButtonOverrideService } from "../../../flow/services/flow-button-override.service";
import { CasingPlanModel, WellSegmentModel, WellDirection, CasingType } from "../new-casing-plan/models/casing-plan.model";
import { isDefined } from "../new-casing-plan/models/casing-plan-functions.model";
import { Button } from "../../../flow/models/flow.model";
import { DocumentManagementService, IAzureBlobSasToken, IUploadFile } from "../../../common/services/document-management.service";
import { CasingPlan2dBaseComponent } from "./casing-plan-2d/casing-plan-2d-base.component";

@Component({
    selector: 'app-casing-plan-schematic',
    templateUrl: './casing-plan-schematic.component.html',
    styleUrls: ['./casing-plan-schematic.component.scss'],
    standalone: false
})
export class CasingPlanSchematicComponent extends BaseControlComponent implements OnInit, OnDestroy {

    static componentName = "CasingPlanSchematicComponent";
    private _showRules: boolean = false;

    get applicationData(): { applicationNumber: string, wellName: string, wellNumber: string } {
        return {
            applicationNumber: this.applicationNumber,
            wellName: this.wellName,
            wellNumber: this.wellNumber
        };
    }

    get applicationNumber() {
        if (this.componentData.config && this.componentData.config.applicationNumber) {
            let location = this.componentData.config.applicationNumber;
            if (location.endsWith(".value") === false) location = `${location}.value`;
            return this.getContextData(location);
        }
            
        return "";
    }

    casingPlan: CasingPlanModel;
    casingTypes = CasingType;
    directions = WellDirection;
    exitingSchematic: boolean = false;
    isAdvancing: boolean = true;
    readonly pageChanging: EventEmitter<boolean> = new EventEmitter<boolean>(false);
    pageIndex: number = 0;

    renderDiagram: boolean = true;

    get currentSegment(): WellSegmentModel {
        if ((this.showSummary && this.segmentIndex < 0) || this.segmentIndex >= this.segments.length) return null;
        return this.segments[this.segmentIndex];
    }

    get glacialDrift(): number {
        return 0; //hardcoding this to be hidden for now; will need to factor in GIS alerts if this is needed
    }


    get hasConductorString(): boolean {
        if (this.casingPlan == null || this.casingPlan.wellSegments == null) return false;
        const conductorIndex = this.casingPlan.wellSegments.findIndex(s => s.casingType.definition === CasingType.Conductor);
        return conductorIndex !== -1;
    }

    get hasShoe(): boolean {
        return this.currentSegment != null && this.currentSegment.casingShoeDepth != null && this.currentSegment.casingShoeDepth > 0;
    }

    get inActiveMineArea(): boolean {
        return false; //hardcoding this to be hidden for now; will need to factor in GIS alerts if this is needed
    }

    get segmentIndex(): number {
        if (this.showSummary) return this.pageIndex - 1;
        return this.pageIndex;
    }

    get segments(): WellSegmentModel[] {
        if (this.casingPlan != null) return this.casingPlan.wellSegments;
        return [];
    }

    get segmentLength(): number {
        if (this.currentSegment != null && this.currentSegment.casingLength != null) return this.currentSegment.casingLength;
        return 0;
    }

    get showDetails(): boolean {
        if (this.componentData == null || this.componentData.config == null) return true;
        return this.componentData.config.display !== "summary";
    }

    get showSegmentRules(): boolean {
        if (this.showSummary && this.pageIndex === 0) {
            return false;
        }
        return this._showRules;
    }

    set showSegmentRules(value: boolean) {
        this._showRules = value;
    }

    get showSummary(): boolean {
        if (this.componentData == null || this.componentData.config == null) return true;
        return this.componentData.config.display !== "detail";
    }

    subscription: Subscription = new Subscription();

    get totalPages(): number {
        let total: number = 0;
        if (this.showSummary) total++;
        if (this.showDetails) total = total + this.segments.length;
        return total;
    }

    get validSegments(): WellSegmentModel[] {
        return this.segments.filter(f => this.segmentValid(f));
    }

    get wellDirection(): WellDirection {
        if (this.casingPlan != null) return this.casingPlan.wellDirection;
        return WellDirection.Vertical;
    }

    get wellName(): string {
        if (this.componentData.config && this.componentData.config.wellName) {
            let location = this.componentData.config.wellName;
            if (location.endsWith(".value") === false) location = `${location}.value`;
            return this.getContextData(location);
        }
        return "";
    }

    get wellNumber(): string {
        if (this.componentData.config && this.componentData.config.wellNumber)  {
            let location = this.componentData.config.wellNumber;
            if (location.endsWith(".value") === false) location = `${location}.value`;
            return this.getContextData(location);
        }
        return "";
    }

    segmentTypeShouldHaveBorehole: string[] = ['COND', 'MS1', 'SURF', 'HOL1', 'I1', 'I2', 'PROD', 'L1'];
    segmentTypeShouldHaveCasing: string[] = ['DP*', 'COND', 'MS1', 'SURF', 'I1', 'I2', 'PROD', 'L1', 'T1'];

    constructor(_contextService: ContextInteractionService, @Optional() private _overrideService: FlowButtonOverrideService, private _docService: DocumentManagementService) {
        super(_contextService);
    }

    ngOnInit() {        
        if (!this.isEdit) {
            super.ngOnInit();
            this.casingPlan = this.getCasingPlanFromContext();
            if (this._overrideService) {
                this._overrideService.incrementFunction = this._incrementFunction.bind(this);
                this._overrideService.decrementFunction = this._decrementFunction.bind(this);
                this._overrideService.setValidatorNextButton(() => { return this.control.invalid; });
            }
            let change$ = this.pageChanging.asObservable().subscribe(
                (change: boolean) => {                   
                    this.renderDiagram = !change;
                },
                (error) => {
                    console.error(error);

                });
            this.subscription.add(change$);
        } else {
            this.formGroup = new FormGroup({});
        }
    }

    ngOnDestroy() {
        if (this._overrideService) this._overrideService.resetOverride();
        if (this.subscription) this.subscription.unsubscribe();
    }

    getCasingPlanFromContext(): CasingPlanModel {
        const location = this.componentData.config.casingPlanLocation;
        const data = this.getContextData(location);
        return data as CasingPlanModel;
    }

    saveCasingPlan(): void {
        const location = this.componentData.config.casingPlanLocation;
        this.contextService.setContextData(location, this.casingPlan, this.componentContext);
    }

    protected _incrementFunction(button: Button): void {
        this.isAdvancing = true;
        this.pageChanging.next(true);
        this.executePageChange(button, true);
    }

    private executePageChange(button: Button, increment: boolean): void {        
        let obs$: Observable<boolean> = of(true);
        const fileName = (this.showSummary && this.pageIndex === 0) ? `${this.flowComponent.workflow.id}_schematic.png` : `${this.flowComponent.workflow.id}_${this.currentSegment.casingType.definition}_detail.png`;
        const img = this.encodeDrawing("image/png");
        if (increment && !this.isEdit) {
            if (img && img !== "") {
                obs$ = this.saveDrawings(fileName, img).pipe(map((token: IAzureBlobSasToken) => {
                    if (this.showSummary && this.pageIndex === 0) {
                        this.casingPlan.schematic = token.blobUri;
                    } else {
                        this.currentSegment.detailDiagram = token.blobUri;
                    }
                    return true;
                }));
            }
        } else {
            if (this.pageIndex == 0) {
                obs$ = this.saveDrawings(fileName, img).pipe(map((token: IAzureBlobSasToken) => {
                    if (this.showSummary) this.casingPlan.schematic = token.blobUri;
                    else this.currentSegment.detailDiagram = token.blobUri;
                    return true;
                }))
            } else {
                obs$ = of(true).pipe(delay(500)); //introduce artificial delay when not saving the image; allows the drawing component time to be destroyed and reinitialized

            }
        }
        const subs$ = obs$.pipe(map((changed: boolean) => {
            if (changed) {
                let newIndex = this.pageIndex;   
                newIndex = (increment) ? this.pageIndex + 1 : this.pageIndex - 1;
                if (newIndex === 0 && this.showSummary) return newIndex;
                if (this.showDetails && newIndex >= 0) {
                    let checkIndex: number = (this.showSummary) ? newIndex - 1 : newIndex;
                    let testSegment = this.segments[checkIndex];
                    while (checkIndex < this.totalPages && testSegment != null && testSegment.hasData == false) {
                        checkIndex += 1;
                        if (checkIndex > this.segments.length) break;
                        testSegment = this.segments[checkIndex];    
                        if (testSegment == null) break;
                    }                    
                    newIndex = (this.showSummary) ? checkIndex + 1 : checkIndex;
                }               
                return newIndex;
            }
            return this.pageIndex;
        })).subscribe(
            (index: number) => {    
                this.pageChanging.next(false);
                if (index >= this.totalPages || index < 0) {
                    this.saveCasingPlan();
                    this.exitingSchematic = true;
                    this._overrideService.callOriginalFunction(button);
                } else {
                    this.pageIndex = index;                   
                }                
            },
            (error) => {
                console.error(error);
                this.pageChanging.next(false);
            }
        );
        this.subscription.add(subs$);
    }

    protected _decrementFunction(button: Button): void {
        this.isAdvancing = false;
        this.pageChanging.next(this.pageIndex > 0);
        this.executePageChange(button, false);
    }

    saveDrawings(name: string, url: string): Observable<IAzureBlobSasToken> {
        var data = url.split(','), mime = data[0].match(/:(.*?);/)[1], bstr = atob(data[1]), n = bstr.length, u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        const fileName = name.replace(" ", "_");
        let blobFile: File;
		blobFile = new File([u8arr], fileName, { type: mime });
        const file = { fileName: `${fileName}`, rawFile: blobFile } as IUploadFile;
        return this.uploadFile(file);
    }

    segmentValid(segment: WellSegmentModel): boolean {
        return segment != null && (this.segmentCasingValid(segment) || this.segmentBoreholeValid(segment));
    }

    segmentBoreholeMissing(segment: WellSegmentModel): boolean {
        return segment != null && !this.segmentBoreholeValid(segment) && this.segmentTypeShouldHaveBorehole.findIndex(t => t === segment.casingType.definition) !== -1;
    }

    segmentCasingMissing(segment: WellSegmentModel): boolean {
        return segment != null && !this.segmentCasingValid(segment) && this.segmentTypeShouldHaveCasing.findIndex(t => t === segment.casingType.definition) !== -1;
    }

    segmentBoreholeValid(segment : WellSegmentModel): boolean {
        return isDefined(segment.boreholeDepth) && isDefined(segment.boreholeDiameter);
    }

    segmentCasingValid(segment: WellSegmentModel): boolean {
        return isDefined(segment.casingLength) && isDefined(segment.casingDiameter);
    }

    private blobToFile(blob: Blob, fileName: string): File {
        const b: any = blob;
        b.lastModifiedDate = new Date();
        b.name = fileName;
        return b as File;
    }

    private encodeDrawing(imageType: string): string {
        const el = document.getElementById("canvas");
        if (el) {
            const canvas = <HTMLCanvasElement>el;
            const url = canvas.toDataURL(imageType, 1.0);
            return url;
        }
        return "";
    }

    private uploadFile(file: IUploadFile): Observable<IAzureBlobSasToken> {
        const uploadComplete = new Subject<IAzureBlobSasToken>();
        return this._docService.provision(file.rawFile.name).pipe(
            tap((data: any) => {
                this._docService.processProvisionedFile(file, data, uploadComplete);
            })
        );
    }
}
